// src/components/HeroBanner.js

import React, { useState, useEffect } from 'react';

const HeroBanner = () => {
    // --- STATE MANAGEMENT ---
    // 1. Text Content State
    const [content, setContent] = useState({
        heading_part1: 'HIRING FOR',
        heading_highlight: 'SCHOOLS ACROSS',
        heading_part2: 'INDIA',
        subheading: 'India’s First Hiring Partner Dedicated to the Education Sector',
        cta_text: 'Apply to Talent Pool',
        pill_text_1: 'Connecting Educators &',
        pill_text_2: 'School Professionals with Trusted Schools'
    });

    // 2. Images State
    const [bgImages, setBgImages] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // 3. Partners (Logos) State
    const [partners, setPartners] = useState([]);

    // --- FETCH DATA FROM API ---
    useEffect(() => {
        // A. Fetch Text Content
        fetch('/api/hero/content')
            .then(res => res.json())
            .then(data => {
                if (data) setContent(data);
            })
            .catch(err => console.error('Failed to fetch hero content:', err));

        // B. Fetch Background Images
        fetch('/api/hero/images')
            .then(res => res.json())
            .then(data => {
                if (data && data.length > 0) {
                    setBgImages(data);
                }
            })
            .catch(err => console.error('Failed to fetch hero images:', err));

        // C. Fetch Partners (Logos)
        fetch('/api/partners')
            .then(res => res.json())
            .then(data => setPartners(data))
            .catch(err => console.error('Failed to fetch partners:', err));

    }, []);

    // --- SLIDESHOW LOGIC (1.5 Seconds) ---
    useEffect(() => {
        if (bgImages.length > 1) {
            const interval = setInterval(() => {
                setCurrentImageIndex(prevIndex => (prevIndex + 1) % bgImages.length);
            }, 1500); // Change image every 1.5 seconds
            return () => clearInterval(interval);
        }
    }, [bgImages]);

    // Determine current background image source
    const currentImageSrc = bgImages.length > 0 
        ? `${bgImages[currentImageIndex].image_path}`
        : "https://images.pexels.com/photos/2774556/pexels-photo-2774556.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"; // Default fallback

    return (
        // FIXED: Increased padding-top (pt-40 for mobile, lg:pt-64 for desktop) to clear the header
        <header className="relative w-full min-h-screen flex flex-col justify-between items-center bg-slate-900 overflow-hidden pt-40 lg:pt-64 pb-10">
            
            {/* ======================= */}
            {/* BACKGROUND LAYERS       */}
            {/* ======================= */}
            <div className="absolute inset-0 z-0">
                <img 
                    src={currentImageSrc}
                    className="w-full h-full object-cover object-center opacity-40 transition-all duration-1000 ease-in-out" 
                    alt="Hero Background" 
                />
            </div>
            {/* Gradient Overlays */}
            <div className="absolute inset-0 z-10 bg-gradient-to-t from-slate-900 via-slate-900/20 to-slate-900/60 pointer-events-none"></div>
            <div className="absolute inset-0 z-10 bg-gradient-to-b from-slate-900/10 via-transparent to-slate-900 pointer-events-none"></div>

            {/* ======================= */}
            {/* MAIN CONTENT (Centered) */}
            {/* ======================= */}
            <div className="relative z-20 flex flex-col items-center justify-center flex-grow w-full max-w-7xl px-4 text-center">
                
                {/* Heading */}
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black text-white uppercase tracking-tighter leading-[1.1] drop-shadow-2xl">
                    {content.heading_part1} <span className="text-orange-500">{content.heading_highlight}</span>  {content.heading_part2}
                </h1>
                  

                {/* Subheading */}
                <p className="mt-4 sm:mt-6 text-base sm:text-xl md:text-2xl text-slate-200 max-w-3xl mx-auto font-medium leading-relaxed drop-shadow-md">
                    {content.subheading}
                </p>

                {/* Info Pill (Stacks on mobile, row on desktop) */}
                <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 sm:px-8 sm:py-3 rounded-2xl sm:rounded-full shadow-2xl">
                    <span className="font-bold text-xs sm:text-base text-white tracking-wide">{content.pill_text_1}</span>
                    <span className="hidden sm:block w-px h-5 bg-white/30"></span>
                    <span className="font-bold text-xs sm:text-base text-white tracking-wide">{content.pill_text_2}</span>
                </div>

                {/* CTA Button */}
                <div className="mt-10 sm:mt-12">
                    <a href="#apply-form" className="inline-block bg-orange-500 text-white text-xs sm:text-sm font-bold uppercase tracking-widest px-8 py-3.5 sm:px-10 sm:py-4 rounded-full shadow-lg shadow-orange-500/20 hover:bg-white hover:text-orange-500 transition-all duration-300 transform hover:-translate-y-1">
                        {content.cta_text}
                    </a>
                </div>
            </div>

            {/* ======================= */}
            {/* FOOTER LOGOS (Bottom)   */}
            {/* ======================= */}
            <div className="relative z-20 w-full px-4 mt-12 sm:mt-0">
                <div className="max-w-4xl mx-auto text-center">
                    <p className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-widest mb-4 sm:mb-6">Hiring Partners Include</p>
                    <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-12 opacity-80">
                        {/* Dynamic Logos Logic */}
                        {partners.length > 0 ? (
                            partners.map((partner) => (
                                <img 
                                    key={partner.id}
                                    src={`${partner.logo_path}`} 
                                    alt="Partner Logo" 
                                    className="h-12 sm:h-16 md:h-20 lg:h-24 object-contain transition-transform hover:scale-105" 
                                />
                            ))
                        ) : (
                            // Fallback if no partners found in DB (Maintains Original Design)
                            <>
                                <img src="/images/mcat_logo.png" alt="mcat" className="h-12 sm:h-16 md:h-20 lg:h-24 brightness-400 object-contain" />
                                <img src="/images/logo1.jpeg" alt="JackNJillEducation" className="h-12 sm:h-16 md:h-20 lg:h-24 brightness-100 object-contain" />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default HeroBanner;